import { useEffect, useState } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { Promisable } from "type-fest";

import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import {
  buildIncidentFormPdfFilename,
  calculateActions,
  getCommandUnit,
  getIncidentTypeSelection,
  IncidentWorkItemIdSchema,
  ParticipantStatusSchema,
  tk,
  type AuditTrailEntryUnbranded,
  type IncidentWorkItemId,
  type IncidentWorkItemWithContext,
} from "@thalamos/vision-common";

import { transferNotCreatedText } from "../../api/incidentWorkItem/api.js";
import api, { apiHooks } from "../../api/index.js";
import { createUserSearch } from "../../api/user/createUserSearch.js";
import { useUser } from "../../Context/UserContext.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { CancelTransferModal } from "../../modals/CancelTransfer/CancelTransferModal.js";
import { InitiateTransferModal } from "../../modals/InitiateTransfer/InitiateTransfer.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorPage } from "../Error/Error.js";
import { fetchEntireAuditTrail } from "./helpers/fetchEntireAuditTrail.js";
import { IncidentDetailsCard } from "./IncidentDetailsCard.js";
import { IncidentTimeline } from "./IncidentTimeline.js";
import { PatientDetailsCard } from "./PatientDetailsCard.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function downloadPdf(
  incident: IncidentWorkItemWithContext,
  onError: (
    key: "downloadPdfFailed" | "downloadPdfError" | "pdfNotAvailable",
  ) => void,
): Promise<void> {
  try {
    const response = await api.incidentWorkItem.getPdf(
      incident.incidentWorkItem.id,
    );
    if (response.status === 409) {
      onError("pdfNotAvailable");
      return;
    }
    if (response.status !== 200) {
      onError("downloadPdfFailed");
      return;
    }
    const dataUri = `data:application/pdf;base64,${response.data}`;
    triggerDownload(
      dataUri,
      buildIncidentFormPdfFilename({
        incidentType: getIncidentTypeSelection({
          incidentWorkItem: incident,
        }).unwrapOr(undefined),
        date: new Date(),
      }),
    );
  } catch {
    onError("downloadPdfError");
  }
}

// ---------------------------------------------------------------------------
// Inner component — receives all data as props (testable / storyable)
// ---------------------------------------------------------------------------

export interface IncidentSummaryPageInnerProps {
  incident: IncidentWorkItemWithContext;
  auditEntries: AuditTrailEntryUnbranded[];
  auditCount: number;
  getAllAuditEntries: () => Promisable<AuditTrailEntryUnbranded[]>;
  isAnonymised: boolean;
  isAdmin: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  canTransfer: boolean;
  canCancelTransfer: boolean;
  onTransfer: () => Promisable<void>;
  onCancelTransfer: () => Promisable<void>;
}

export function IncidentSummaryPageInner({
  incident,
  auditEntries,
  auditCount,
  getAllAuditEntries,
  isAnonymised,
  isAdmin,
  onLoadMore,
  hasMore,
  loadingMore,
  canTransfer,
  canCancelTransfer,
  onTransfer,
  onCancelTransfer,
}: IncidentSummaryPageInnerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const notifications = useToastNotifications();

  const { incidentWorkItem } = incident;
  const isOpen = incidentWorkItem.status !== "ended";

  return (
    <Box
      data-testid="incident-summary-page"
      sx={{
        width: "100%",
        // On desktop fill the viewport so the sidebar sticks and timeline scrolls
        height: { md: "100vh" },
        overflow: { md: "hidden" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Top section (back + hero + notice + action bar) ── */}
      <Box
        sx={{
          flexShrink: 0,
          maxWidth: 1400,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 2, sm: 3 },
          pb: 1,
        }}
      >
        {/* Back button */}
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              navigate(createRoute.home());
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
            data-testid="back-button"
          >
            {t(tk("incidentSummary.backToIncidents"))}
          </Button>
        </Box>

        {/* Anonymised notice */}
        {isAnonymised && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="anonymised-notice">
            {t(tk("incidentSummary.anonymisedNotice"))}
          </Alert>
        )}

        {/* Action bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
            gap: 2,
            mb: 2,
          }}
          data-testid="action-bar"
        >
          <ButtonGroup
            variant="contained"
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            {!isAnonymised && (
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => {
                  void downloadPdf(incident, (key) => {
                    notifications.show(t(tk(`incidentSummary.${key}`)), {
                      severity: "error",
                    });
                  });
                }}
                disabled={false}
                data-testid="download-pdf-btn"
              >
                {t(tk("incidentSummary.downloadPdf"))}
              </Button>
            )}
            {canCancelTransfer && (
              <Button
                startIcon={<CloseIcon />}
                onClick={() => {
                  void onCancelTransfer();
                }}
                variant="outlined"
                data-testid="cancel-transfer-btn"
              >
                {t(tk("incidentSummary.cancelTransfer"))}
              </Button>
            )}
            {canTransfer && (
              <Button
                startIcon={<SwapHorizIcon />}
                onClick={() => {
                  void onTransfer();
                }}
                variant="outlined"
                data-testid="transfer-btn"
              >
                {t(tk("incidentSummary.transfer"))}
              </Button>
            )}
            {isAdmin && (
              <Button
                startIcon={<EditCalendarIcon />}
                onClick={() => {
                  navigate(
                    createRoute.incidentAdminEditReportingFieldsPage(
                      incidentWorkItem.id,
                    ),
                  );
                }}
                variant="outlined"
                data-testid="admin-edit-reporting-fields-btn"
              >
                {t(tk("adminEditReportingFields.launchButton"))}
              </Button>
            )}
          </ButtonGroup>
        </Box>
      </Box>

      {/* ── Two-column layout ── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          maxWidth: 1400,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          pb: { xs: 2, sm: 3 },
          // Mobile: normal flow. Desktop: the grid fills remaining height.
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "380px 1fr" },
          gap: 3,
          alignItems: "start",
          overflow: { xs: "visible", md: "hidden" },
        }}
      >
        {/* Left column — info cards (sticky on desktop) */}
        <Stack
          sx={{
            gap: 3,
            overflow: { md: "auto" },
            maxHeight: { md: "100%" },
            pr: { md: 1 },
          }}
        >
          <IncidentDetailsCard
            incident={incident}
            defaultExpanded={!isMobile}
          />
          {!isAnonymised && (
            <PatientDetailsCard
              incident={incident}
              defaultExpanded={!isMobile}
            />
          )}
        </Stack>

        {/* Right column — scrollable timeline */}
        <Box
          sx={{
            overflow: { md: "auto" },
            maxHeight: { md: "100%" },
          }}
        >
          <IncidentTimeline
            entries={auditEntries}
            totalCount={auditCount}
            getAllEntries={getAllAuditEntries}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            loadingMore={loadingMore}
            isOpen={isOpen}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Outer component — data fetching orchestrator
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;

function IncidentSummaryDataFetcher({
  incidentId,
}: {
  incidentId: IncidentWorkItemId;
}) {
  const { user, loading: loadingUser } = useUser();
  const { t } = useTranslation();
  const notifications = useToastNotifications();

  const [offset, setOffset] = useState(0);
  const [allEntries, setAllEntries] = useState<AuditTrailEntryUnbranded[]>([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCancelTransferModal, setShowCancelTransferModal] = useState(false);

  const [
    { data: incident, loading: loadingIncident, error: incidentError },
    refetchIncident,
  ] = apiHooks.incidentWorkItem.getSummary(incidentId);

  const [{ data: auditData, loading: loadingAudit, error: auditError }] =
    apiHooks.incidentWorkItem.getAuditTrail(incidentId, {
      limit: PAGE_SIZE,
      offset,
    });

  // Accumulate audit entries as user pages through, in chronological order
  useEffect(() => {
    if (auditData?.entries) {
      setAllEntries((prev) => {
        const incoming = [...auditData.entries].reverse();
        if (offset === 0) return incoming;
        const existingIds = new Set(prev.map((e) => e.id));
        const newEntries = incoming.filter((e) => !existingIds.has(e.id));
        return [...newEntries, ...prev];
      });
    }
  }, [auditData, offset]);

  const handleLoadMore = () => {
    setOffset((prevOffset) => prevOffset + PAGE_SIZE);
  };

  if (loadingUser || loadingIncident || (loadingAudit && offset === 0)) {
    return (
      <Stack
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
          gap: 3,
        }}
      >
        <Skeleton variant="rounded" height={48} width={180} />
        <Skeleton variant="rounded" height={100} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
            gap: 3,
          }}
        >
          <Stack sx={{ gap: 3 }}>
            <Skeleton variant="rounded" height={200} />
            <Skeleton variant="rounded" height={180} />
          </Stack>
          <Skeleton variant="rounded" height={400} />
        </Box>
      </Stack>
    );
  }

  if (incidentError || auditError || !incident) {
    return <ErrorPage />;
  }

  const isAnonymised = !!user?.isAdminSession;
  const totalCount = auditData?.count ?? 0;
  const hasMore = allEntries.length < totalCount;

  const transferActions = user
    ? calculateActions({
        incidentWorkItem: incident,
        user,
        asOfDateTime: dayjs(),
      }).transfers
    : undefined;

  const pendingTransferOfficer = incident.participants.find(
    (participant) =>
      participant.status === ParticipantStatusSchema.enum.pendingTransfer,
  );

  return (
    <>
      <IncidentSummaryPageInner
        incident={incident}
        auditEntries={allEntries}
        auditCount={totalCount}
        getAllAuditEntries={() =>
          hasMore
            ? fetchEntireAuditTrail((page) =>
                api.incidentWorkItem.getAuditTrail(incidentId, page),
              )
            : allEntries
        }
        isAnonymised={isAnonymised}
        isAdmin={!!user?.isAdminSession}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        loadingMore={loadingAudit && offset > 0}
        canTransfer={transferActions?.canInitiate ?? false}
        canCancelTransfer={transferActions?.canRevoke ?? false}
        onTransfer={() => {
          setShowTransferModal(true);
        }}
        onCancelTransfer={() => {
          setShowCancelTransferModal(true);
        }}
      />

      {showCancelTransferModal && pendingTransferOfficer && (
        <CancelTransferModal
          open={showCancelTransferModal}
          close={() => {
            setShowCancelTransferModal(false);
          }}
          receivingOfficer={pendingTransferOfficer.name}
          incidentWorkItemId={incidentId}
          onTransferCancelled={async () => {
            setShowCancelTransferModal(false);
            await refetchIncident();
          }}
        />
      )}

      {showTransferModal && (
        <InitiateTransferModal
          open={showTransferModal}
          onClose={() => {
            setShowTransferModal(false);
          }}
          incidentWorkItemId={incidentId}
          searchUsers={createUserSearch(
            getCommandUnit({ incidentWorkItem: incident })?.id,
          )}
          isAdmin={true}
          onConfirm={async (email: string) => {
            const createTransfer = {
              receivingOfficerEmail: email,
              incidentWorkItemId: incidentId,
            };

            const res =
              await api.incidentWorkItem.createTransfer(createTransfer);
            if (res.status === 204) {
              notifications.show(
                t("transfer.initiateTransfer.confirmToast", { email }),
                { severity: "success" },
              );
              setShowTransferModal(false);
              await refetchIncident();
            } else {
              notifications.show(
                transferNotCreatedText(res.data) ?? "Transfer failed",
                { severity: "error" },
              );
            }
          }}
        />
      )}
    </>
  );
}

export function IncidentSummaryPage() {
  const params = useParams();
  const parseResult = IncidentWorkItemIdSchema.safeParse(params.id);

  if (!parseResult.success) {
    return <ErrorPage />;
  }

  return <IncidentSummaryDataFetcher incidentId={parseResult.data} />;
}
